import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BadgeCheckIcon, SearchIcon, ShieldAlertIcon, UsersIcon } from 'lucide-react';
import { Panel } from '../ui/Panel';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { ChoiceGroup } from '../ui/ChoiceGroup';
import { Field, Input } from '../ui/Field';
import { EmptyState } from '../ui/EmptyState';
import { ReportControls } from '../trust/ReportControls';
import { useSocial } from '../../contexts/SocialContext';
import { useSpaceAccess } from '../../hooks/useSpaceAccess';
import type { CommunityCategory, SocialCommunity } from '../../types/social';
const CATEGORIES = ['All', 'Academic', 'Social', 'Interest', 'Career', 'Campus'] as const;
export function CommunitiesPanel() {
  const {
    communities,
    toggleCommunity,
    toggleFollowCommunity,
    interests,
    toggleInterest
  } = useSocial();
  const [category, setCategory] = useState<(typeof CATEGORIES)[number]>('All');
  const [query, setQuery] = useState('');
  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return communities.filter(community => {
      if (category !== 'All' && community.category !== category as CommunityCategory) return false;
      if (!needle) return true;
      return `${community.name} ${community.description}`.toLowerCase().includes(needle);
    });
  }, [category, communities, query]);
  return <div className="space-y-5" data-id="element-5525" data-mp="2pugbu0vs4v0a">
      <Panel title="Your interests" description="These drive what gets recommended to you. Turn one off and the suggestions change." data-id="element-5526" data-mp="2pvhjrwhi11ds">
        <ul className="flex flex-wrap gap-2" data-id="element-5527" data-mp="2h13803w6g8zf">
          {['Engineering', 'Photography', 'Entrepreneurship', 'Technology', 'Sports', 'Books', 'Volunteering'].map(interest => {
          const on = interests.includes(interest);
          return <li key={interest} data-id="element-5528" data-mp="226ovmm74bxtq">
                <button type="button" aria-pressed={on} onClick={() => toggleInterest(interest)} className={`min-h-[44px] rounded-full border px-4 text-body-sm font-medium ${on ? 'border-brand-700 bg-brand-700 text-white' : 'border-line bg-canvas text-ink hover:border-brand-300'}`} data-id="element-5529" data-mp="1sggmzmbgad44">
                  {interest}
                </button>
              </li>;
        })}
        </ul>
      </Panel>

      <Panel title="Find a community" data-id="element-5530" data-mp="22f5ucl5q0f64">
        <Field label="Search" htmlFor="com-search" data-id="element-5531" data-mp="22e4iwfkslqnv">
          <div className="relative" data-id="element-5532" data-mp="22dcw9u96b39q">
            <SearchIcon aria-hidden className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-ink-subtle" data-id="element-5533" data-mp="26fzgpa3illww" />
            <Input id="com-search" value={query} onChange={event => setQuery(event.target.value)} placeholder="Department, interest or club name" className="pl-11" data-id="element-5534" data-mp="2ywtq2vt1jqj4" />
          </div>
        </Field>
        <div className="mt-4" data-id="element-5535" data-mp="ih5sfomc0n7u">
          <ChoiceGroup label="Category" options={CATEGORIES} value={category} onChange={setCategory} data-id="element-5536" data-mp="x8fppdwe0i83" />
        </div>
      </Panel>

      {shown.length === 0 ? <Panel data-id="element-5537" data-mp="4ka3sntlkn6o">
          <EmptyState icon={<UsersIcon className="h-5 w-5" data-id="element-5539" data-mp="4l2246ekwz0n" />} title="No communities match" description="Try a different category, or ask your department to open one." data-id="element-5538" data-mp="4jqxy5h81p90" />
        </Panel> : <ul className="grid gap-4 lg:grid-cols-2" data-id="element-5540" data-mp="9ibp7fehwylb">
          {shown.map(community => <CommunityCard key={community.id} community={community} onJoin={() => toggleCommunity(community.id)} onFollow={() => toggleFollowCommunity(community.id)} data-id="element-5541" data-mp="1pwjyw9e63dfj" />)}
        </ul>}
    </div>;
}

/**
 * One community. Membership requirements come from the configured access
 * policy, so a community restricted to verified students enforces that itself.
 */
function CommunityCard({
  community,
  onJoin,
  onFollow
}: {
  community: SocialCommunity;
  onJoin: () => void;
  onFollow: () => void;
}) {
  const {
    policy,
    decision,
    allowed
  } = useSpaceAccess(community.id);
  return <li data-id="element-5542" data-mp="dezccdgrptyj">
              <Panel className="h-full" bodyClassName="flex h-full flex-col p-5 sm:p-6" data-id="element-5543" data-mp="ddeo15l1r794">
                <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2" data-id="element-5544" data-mp="ic7wgjlh882f">
                  <div className="min-w-0" data-id="element-5545" data-mp="n75bq89cndnr">
                    <p className="flex flex-wrap items-center gap-2 text-body-lg font-semibold text-ink" data-id="element-5546" data-mp="r75s3yg0dyxg">
                      {community.name}
                      {community.official && <BadgeCheckIcon aria-label="Official community" className="h-5 w-5 shrink-0 text-brand-700" data-id="element-5547" data-mp="114afg1llc0fu" />}
                    </p>
                    <p className="cz-num text-body-sm text-ink-muted" data-id="element-5548" data-mp="1682trfd7tib6">
                      {community.members.toLocaleString()} members · {community.scope}
                    </p>
                  </div>
                  <Badge plain data-id="element-5549" data-mp="xasgxpkr1l45">{community.category}</Badge>
                </div>

                <p className="mt-2 max-w-[70ch] text-body-sm text-ink" data-id="element-5550" data-mp="1txx4nrdz4wss">{community.description}</p>

                <p className="mt-2 text-caption text-ink-subtle" data-id="element-5551" data-mp="1p1ge6qjay6cj">
                  Moderated by {community.moderators.join(', ')} · {community.activity}
                </p>

                {!community.official && <p className="mt-2 flex items-start gap-2 text-caption text-ink-muted" data-id="element-5552" data-mp="1g7lazo1qqpzj">
                    <ShieldAlertIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0" data-id="element-5553" data-mp="2craqn6zplsw9" />
                    Student-run. Not an official institutional community.
                  </p>}

                {policy && <div className={`mt-3 rounded-md border p-4 ${allowed ? 'border-ok-200 bg-ok-50' : 'border-line bg-canvas'}`} data-id="element-5554" data-mp="63tp4fq74yij">
                    <p className="text-body font-semibold text-ink" data-id="element-5555" data-mp="1ah9uw7eq42g">{policy.requirement}</p>
                    {decision && <p className="mt-1 max-w-[70ch] text-body-sm text-ink" data-id="element-5556" data-mp="3jfc56xwjrqvw">{decision.message}</p>}
                    {!allowed && decision?.remedy && <p className="mt-1.5 max-w-[70ch] text-body-sm text-ink-muted" data-id="element-5557" data-mp="39ipmaens79wi">
                        {decision.remedy}
                      </p>}
                    {!allowed && <Link to="/identity" className="mt-3 inline-block" data-id="element-5558" data-mp="17qkxhc1kwsq0">
                        <Button size="sm" variant="secondary" data-id="element-5559" data-mp="1cpxn24ju4dwa">
                          Verify your academic identity
                        </Button>
                      </Link>}
                  </div>}

                <div className="mt-auto flex flex-wrap items-center gap-2 pt-4" data-id="element-5560" data-mp="2sa6jn28p3mhr">
                  <Button variant={community.joined ? 'secondary' : 'primary'} size="sm" onClick={onJoin} disabled={!community.joined && !allowed} data-id="element-5561" data-mp="36y1cqjqm7svd">
                    {community.joined ? 'Leave' : allowed ? 'Join' : 'Requirement not met'}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={onFollow} aria-pressed={community.following} data-id="element-5562" data-mp="3pwm14414gk5b">
                    {community.following ? 'Following' : 'Follow'}
                  </Button>
                  <Link to="/community" className="text-body-sm font-medium text-brand-700 hover:underline" data-id="element-5563" data-mp="1qjhys358pyzd">
                    Members and groups
                  </Link>
                  <div className="ml-auto" data-id="element-5564" data-mp="19onj5oy4prba">
                    <ReportControls subject={community.name} surface="group" data-id="element-5565" data-mp="19nchbuo8utmt" />
                  </div>
                </div>
                {community.joined && !community.following && <p className="mt-2 text-caption text-ink-subtle" data-id="element-5566" data-mp="1jegvis63iagl">
                    You are a member but not following, so posts here will not reach your feed.
                  </p>}
              </Panel>
            </li>;
}